import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Avatar,
  AvatarGroup,
  Button,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  StatusChip,
} from '@/design/components'
import type { Column, SortState } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { EMPTY, formatRelative, plural } from '@/lib/format'
import {
  toItems,
  useCaseFilters,
  useCaseStats,
  useCasesQuery,
  useLawyers,
  useUpcomingDeadlines,
} from '../hooks'
import { STATUS_LABEL, STATUS_OPTIONS, STATUS_VARIANT, kindLabel } from '../status'
import type { CaseRow, DeadlineLike } from '../types'
import { DeadlineCell } from '../components/DeadlineCell'
import { NewCaseModal } from '../components/NewCaseModal'

const PAGE_SIZE = 50
const ANY = ''

/** „47 spraw w toku · 12 oczekuje na termin · 6 zawieszonych" (mockup). */
function subtitleFrom(stats: { in_progress: number; awaiting: number; suspended: number } | undefined): string {
  if (!stats) return 'Wczytywanie…'
  return [
    `${stats.in_progress} ${plural(stats.in_progress, 'sprawa', 'sprawy', 'spraw')} w toku`,
    `${stats.awaiting} oczekuje na termin`,
    `${stats.suspended} ${plural(stats.suspended, 'zawieszona', 'zawieszone', 'zawieszonych')}`,
  ].join(' · ')
}

export function CasesListPage() {
  const navigate = useNavigate()
  const { can } = useMe()

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<SortState>({ key: 'activity', direction: 'desc' })
  const [client, setClient] = useState(ANY)
  const [status, setStatus] = useState(ANY)
  const [lawyerId, setLawyerId] = useState(ANY)
  const [court, setCourt] = useState(ANY)
  const [kind, setKind] = useState(ANY)
  const [newCaseOpen, setNewCaseOpen] = useState(false)

  // Wejście z pulpitu („Nowa sprawa" → /sprawy?nowa=1) otwiera modal i czyści query param,
  // żeby powrót ze strony sprawy nie otwierał go ponownie.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('nowa') !== '1') return
    const next = new URLSearchParams(searchParams)
    next.delete('nowa')
    setSearchParams(next, { replace: true })
    if (can('cases.create')) setNewCaseOpen(true)
  }, [searchParams, setSearchParams, can])

  const stats = useCaseStats()
  const filters = useCaseFilters()
  const lawyers = useLawyers()

  const listing = useCasesQuery({
    page,
    size: PAGE_SIZE,
    status: status || undefined,
    client: client || undefined,
    lawyer_id: lawyerId ? Number(lawyerId) : undefined,
    court: court || undefined,
    kind: kind || undefined,
    sort: sort.key,
    direction: sort.direction,
  })

  const rows = listing.data?.items ?? []
  const caseIds = useMemo(() => rows.map((row) => row.id), [rows])
  const deadlines = useUpcomingDeadlines(caseIds)

  /** case_id → najbliższy termin (moduł `deadlines` bywa wyłączony → mapa pusta). */
  const deadlineByCase = useMemo(() => {
    const map = new Map<number, DeadlineLike>()
    for (const deadline of toItems<DeadlineLike>(deadlines.data)) {
      const id = deadline.case_id
      if (typeof id !== 'number') continue
      const current = map.get(id)
      const next = deadline.due_date ?? deadline.date ?? ''
      const currentDate = current ? (current.due_date ?? current.date ?? '') : ''
      if (!current || (next && next < currentDate)) map.set(id, deadline)
    }
    return map
  }, [deadlines.data])

  const deadlinesAvailable = deadlines.data !== null && deadlines.data !== undefined

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value)
    setPage(1)
  }

  const columns: Column<CaseRow>[] = [
    {
      key: 'signature',
      header: 'Sygnatura',
      width: '132px',
      sortable: true,
      render: (row) => <span className="t-mono whitespace-nowrap text-ink">{row.signature}</span>,
    },
    {
      key: 'title',
      header: 'Sprawa / Klient',
      sortable: true,
      render: (row) => (
        <div className="min-w-0 leading-tight">
          <p className="truncate font-semibold text-ink">{row.title}</p>
          <p className="mt-0.5 truncate text-small text-muted">
            {[row.client_name, row.court_short ?? row.court].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '150px',
      sortable: true,
      render: (row) => (
        <StatusChip variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</StatusChip>
      ),
    },
    {
      key: 'lead',
      header: 'Prawnik prowadzący',
      width: '190px',
      render: (row) =>
        row.lead ? (
          <span className="flex items-center gap-2.5 whitespace-nowrap">
            <Avatar name={row.lead.full_name} size="md" />
            <span className="text-ink">{row.lead.full_name}</span>
          </span>
        ) : (
          <span className="text-muted">{EMPTY}</span>
        ),
    },
    {
      key: 'team',
      header: 'Zespół',
      width: '108px',
      render: (row) => {
        const others = row.team.filter((member) => member.id !== row.lead?.id)
        return others.length > 0 ? (
          <AvatarGroup names={others.map((member) => member.full_name)} size="sm" max={4} />
        ) : (
          <span className="text-muted">{EMPTY}</span>
        )
      },
    },
    {
      key: 'deadline',
      header: 'Najbliższy termin',
      width: '176px',
      render: (row) => (
        <DeadlineCell
          deadline={deadlineByCase.get(row.id)}
          status={row.status}
          moduleAvailable={deadlinesAvailable}
        />
      ),
    },
    {
      key: 'activity',
      header: 'Aktywność',
      width: '116px',
      sortable: true,
      render: (row) => <span className="text-muted">{formatRelative(row.updated_at)}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        title="Sprawy"
        subtitle={subtitleFrom(stats.data)}
        actions={
          <>
            <Button variant="secondary" disabled title="Eksport rejestru — wkrótce">
              Eksport
            </Button>
            {can('cases.create') ? (
              <Button variant="primary" onClick={() => setNewCaseOpen(true)}>
                Nowa sprawa
              </Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <Select
          block={false}
          inlineLabel="Klient"
          value={client}
          onChange={(event) => resetPage(setClient)(event.target.value)}
          options={[
            { value: ANY, label: 'wszyscy' },
            ...(filters.data?.clients ?? []).map((value) => ({ value, label: value })),
          ]}
        />
        <Select
          block={false}
          inlineLabel="Status"
          value={status}
          onChange={(event) => resetPage(setStatus)(event.target.value)}
          options={[{ value: ANY, label: 'wszystkie' }, ...STATUS_OPTIONS]}
        />
        <Select
          block={false}
          inlineLabel="Prawnik"
          value={lawyerId}
          onChange={(event) => resetPage(setLawyerId)(event.target.value)}
          options={[
            { value: ANY, label: 'dowolny' },
            ...(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.full_name,
            })),
          ]}
        />
        <Select
          block={false}
          inlineLabel="Sąd"
          value={court}
          onChange={(event) => resetPage(setCourt)(event.target.value)}
          options={[
            { value: ANY, label: 'wszystkie' },
            ...(filters.data?.courts ?? []).map((value) => ({ value, label: value })),
          ]}
        />
        <Select
          block={false}
          inlineLabel="Rodzaj"
          value={kind}
          onChange={(event) => resetPage(setKind)(event.target.value)}
          options={[
            { value: ANY, label: 'wszystkie' },
            ...(filters.data?.kinds ?? []).map((value) => ({ value, label: kindLabel(value) })),
          ]}
        />
        <Button variant="link" className="ml-auto" disabled title="Zapisane widoki — wkrótce">
          Zapisz widok
        </Button>
      </div>

      <DataTable<CaseRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/sprawy/${row.id}`)}
        sort={sort}
        onSortChange={setSort}
        total={listing.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={listing.isPending}
        empty={
          <EmptyState
            title="Brak spraw"
            description="Zmień filtry albo załóż nową sprawę."
            action={
              can('cases.create') ? (
                <Button variant="primary" onClick={() => setNewCaseOpen(true)}>
                  Nowa sprawa
                </Button>
              ) : null
            }
          />
        }
      />

      <NewCaseModal
        open={newCaseOpen}
        onClose={() => setNewCaseOpen(false)}
        onCreated={(created) => {
          setNewCaseOpen(false)
          navigate(`/sprawy/${created.id}`)
        }}
      />
    </>
  )
}
